<template>
  <div class="file-preview-container">    
    <!-- 图片预览 -->
    <div v-if="fileType.type === 'image'" class="preview-image">
      <img 
        :src="item.document" 
        :alt="item.headline || '图片'"
        @error="handleImageError"
      />
    </div>
    
    <!-- 音频预览 -->
    <div v-else-if="fileType.type === 'audio'" class="preview-audio">
      <audio 
        controls 
        :src="item.document"
        preload="metadata"
      >
        您的浏览器不支持音频播放
      </audio>
    </div>
    
    <!-- 视频预览 -->
    <div v-else-if="fileType.type === 'video'" class="preview-video">
      <video 
        controls 
        :src="item.document"
        preload="metadata"
        :poster="getVideoPoster(item.document)"
      >
        您的浏览器不支持视频播放
      </video>
    </div>
    
    <!-- PDF文档预览 -->
    <div v-else-if="fileType.category === 'pdf'" class="preview-pdf">
      <div class="pdf-viewer">
        <iframe 
          :src="`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(item.document)}`"
          frameborder="0"
          width="100%"
          height="500"
        ></iframe>
      </div>
    </div>
    
    <!-- 其他文档类型（提供下载） -->
    <div v-else class="preview-other">
      <div class="document-icon">
        <i :class="`icon ${fileIcon}`" style="font-size: 48px;"></i>
        <div class="file-name">{{ getFileName(item.document) }}</div>
      </div>
      <div class="file-info">
        <span>文档类型: {{ fileType.extension || '未知格式' }}</span>
        <div class="actions">
          <a 
            :href="item.document" 
            target="_blank" 
            class="view-btn"
            v-if="canPreviewInBrowser(fileType.extension)"
          >
            在浏览器中查看
          </a>
          <a 
            :href="item.document" 
            target="_blank" 
            class="download-btn"
            :download="getFileName(item.document)"
          >
            下载文件
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFileType, getFileIcon, getFileExtension } from '@/utils/fileType'

export default {
  name: 'FilePreview',
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({
        document: null,
        headline: ''
      })
    }
  },
  data() {
    return {
      imageLoadError: false
    }
  },
  computed: {
    fileType() {
      return getFileType(this.item.document)
    },
    fileIcon() {
      return getFileIcon(this.item.document)
    }
  },
  methods: {
    getFileExtension,
    
    // 从URL中提取文件名
    getFileName(url) {
      if (!url) return '未命名文件'
      const fileName = url.split('/').pop()
      return fileName.split('?')[0] || '未命名文件'
    },
    
    // 获取视频封面（如果有的话）
    getVideoPoster(url) {
      // 这里可以返回默认封面或根据业务需要处理
      return ''
    },
    
    // 处理图片加载失败
    handleImageError(event) {
      this.imageLoadError = true
      // 可以设置一个默认的占位图
      event.target.src = 'https://via.placeholder.com/400x300?text=图片加载失败'
    },
    
    // 判断是否可以在浏览器中预览
    canPreviewInBrowser(extension) {
      const browserPreviewable = ['txt', 'md', 'json', 'xml', 'csv', 'html', 'htm']
      return browserPreviewable.includes(extension)
    },
    
    // 获取文件大小（需要服务器支持）
    async getFileSize(url) {
      try {
        const response = await fetch(url, { method: 'HEAD' })
        const size = response.headers.get('content-length')
        return this.formatFileSize(size)
      } catch (error) {
        console.error('获取文件大小失败:', error)
        return '未知大小'
      }
    },
    
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0 || !bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.file-preview-container {
  background: #fff;
padding: 0 16px;

overflow-y: auto;
}

.no-file {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #999;
}

.no-file .icon {
  font-size: 48px;
  margin-right: 12px;
}

.preview-image,
.preview-audio,
.preview-video,
.preview-pdf,
.preview-other {
  margin-bottom: 16px;
}

.preview-image img {
  max-width: 100%;
  display: block;
  margin: 0 auto 16px;
}

.preview-audio audio{
    width: 50%;
    margin-top: 200px;
    margin-left: 100px;
    display: block;
}
.preview-video{
  margin-bottom: 16px;
}
 video{
    width: 100%;
 }

.preview-video video {
  max-height: 500px;
  background: #000;
}

.preview-pdf .pdf-viewer {
  margin-bottom: 16px;
  border: 1px solid #ddd;
}

.preview-other .document-icon {
  text-align: center;
  padding: 32px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 16px;
}

.preview-other .file-name {
  margin-top: 12px;
  font-size: 14px;
  color: #666;
  word-break: break-all;
}

.file-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 14px;
}

.file-info .icon {
  margin-right: 8px;
  color: #666;
}

.actions {
  display: flex;
  gap: 8px;
}

.view-btn,
.download-btn {
  padding: 6px 12px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 12px;
  transition: all 0.3s;
}

.view-btn {
  background: #1890ff;
  color: white;
  border: 1px solid #1890ff;
}

.download-btn {
  background: white;
  color: #1890ff;
  border: 1px solid #1890ff;
}

.view-btn:hover {
  background: #40a9ff;
}

.download-btn:hover {
  background: #e6f7ff;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .file-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>